import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'

import { EmptySearchResult } from '@/components/EmptySearchResult'
import InputSearch from '@/components/InputSearch'
import { Table } from '@/components/Table'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { useAuthStore } from '@/features/auth/authStore'
import { useCompanyList } from '@/features/company'
import { useProjectList, useProjectsStore } from '@/features/project'
import { getProjectColumns, ProjectAction } from '@/features/project/columns'
import DeleteProjectDialog from '@/features/project/components/DeleteProjectDialog'
import ProjectDialog from '@/features/project/components/ProjectDialog'
import { Project } from '@/features/project/types'
import { ERoleType } from '@/features/user-management/constants'
import { useDebounce } from '@/hooks/useDebounce'
import { SortOrder } from '@/types/common'
import {
  DEFAULT_GET_ALL_LIMIT,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_DELAY_MS,
  SortDirection,
} from '@/utils/constants'
import { buildProjectRoute } from '@/utils/routes'

const ProjectListPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    searchText,
    filters,
    page,
    rowsPerPage,
    sort,
    editingProject,
    dialogOpen,
    deletingProject,
    setSearchText,
    setFilter,
    setPage,
    setSort,
    setEditingProject,
    setDeletingProject,
    setDialogOpen,
    reset,
  } = useProjectsStore()

  // Get user roles to check permissions
  const roles = useAuthStore((state) => state.roles)
  const getCompaniesFromRoles = useAuthStore(
    (state) => state.getCompaniesFromRoles
  )

  const isTeamMember = useMemo(
    () =>
      roles &&
      roles.length > 0 &&
      roles.every((role) => role.role_name === ERoleType.TEAM_MEMBER),
    [roles]
  )

  const isOrgAdmin = useMemo(
    () =>
      roles?.some((role) => role.role_name === ERoleType.ORGANIZATION_ADMIN),
    [roles]
  )

  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  const searchQuery = useMemo(() => {
    const trimmed = debouncedSearchText?.trim() || ''
    return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ''
  }, [debouncedSearchText])

  const { data: companiesResponse } = useCompanyList({
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    sort: { column: 'name', direction: SortDirection.ASC },
    enabled: isOrgAdmin,
  })

  const companies = useMemo(() => {
    if (isOrgAdmin) {
      return (companiesResponse?.data || [])
        .map((c) => c.name)
        .sort((a, b) => a.localeCompare(b))
    }

    // For non-admin users, get companies from roles
    const companiesFromRoles = getCompaniesFromRoles()
    return companiesFromRoles
      .map((c) => c.name)
      .sort((a, b) => a.localeCompare(b))
  }, [isOrgAdmin, companiesResponse, getCompaniesFromRoles])

  const stableFilters = useMemo(
    () => ({
      searchName: searchQuery,
      companyName: filters.companyName,
      companyId: filters.companyId,
    }),
    [searchQuery, filters.companyName, filters.companyId]
  )

  const stableSort = useMemo(
    () => ({
      column: sort.column,
      direction: sort.direction,
    }),
    [sort.column, sort.direction]
  )

  useEffect(() => {
    return () => {
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset page when search query changes
  useEffect(() => {
    setPage(0)
  }, [searchQuery, setPage])

  // Reset page when filters change
  useEffect(() => {
    setPage(0)
  }, [filters.companyId, setPage])

  const { data, isLoading } = useProjectList({
    search: searchQuery,
    page,
    rowsPerPage,
    sort: stableSort,
    filters: stableFilters,
  })
  const projects = data?.data || []
  const totalCount = data?.metadata?.total || 0
  const totalPages = Math.ceil(totalCount / rowsPerPage)

  const handleActionClick = useCallback(
    (action: ProjectAction, item: Project) => {
      switch (action) {
        case 'view-detail':
          navigate(buildProjectRoute(item.id))
          break
        case 'edit':
          setEditingProject(item)
          break
        case 'delete':
          setDeletingProject(item)
          break
        default:
          break
      }
    },
    [navigate, setEditingProject, setDeletingProject]
  )

  const columns = useMemo(() => {
    const allColumns = getProjectColumns(handleActionClick, t)
    // Filter out the actions column for team members
    return isTeamMember
      ? allColumns.filter((col) => col.id !== 'actions')
      : allColumns
  }, [handleActionClick, t, companiesResponse?.data, isTeamMember])

  const handleSortChange = useCallback(
    (colId: keyof Project | null, dir: SortOrder) => {
      setSort({
        column: colId,
        direction: dir,
      })
    },
    [setSort]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value)
    },
    [setSearchText]
  )

  const handleCompanyChange = useCallback(
    (companyName: string | null) => {
      if (companyName) {
        let selectedCompany
        if (isOrgAdmin) {
          selectedCompany = companiesResponse?.data?.find(
            (c) => c.name === companyName
          )
        } else {
          // For non-admin users, get company from roles
          const companiesFromRoles = getCompaniesFromRoles()
          selectedCompany = companiesFromRoles.find(
            (c) => c.name === companyName
          )
        }

        if (
          filters.companyName !== companyName ||
          filters.companyId !== selectedCompany?.id
        ) {
          setFilter({
            companyName,
            companyId: selectedCompany?.id || null,
          })
        }
      } else if (filters.companyName !== null || filters.companyId !== null) {
        setFilter({ companyName: null, companyId: null })
      }
    },
    [
      setFilter,
      isOrgAdmin,
      companiesResponse,
      getCompaniesFromRoles,
      filters.companyName,
      filters.companyId,
    ]
  )

  const handleAddNewClick = () => {
    setDialogOpen(true)
  }

  const isSearching = Boolean(searchQuery)

  const noDataMessage = useMemo(() => {
    if (isSearching) {
      return <EmptySearchResult translationKey="project" />
    }
    return null
  }, [isSearching])

  return (
    <div className="flex flex-col space-y-6">
      {/* MARK: Page Title */}
      <div className="flex justify-between flex-shrink-0">
        <div>
          <h1 className="m-0 mb-1 font-semibold text-display-xs">
            {t('project.title')}
          </h1>
          <p className="m-0 text-md text-muted-foreground">
            {t('project.description')}
          </p>
        </div>
        {!isTeamMember && (
          <div>
            <Button
              onClick={handleAddNewClick}
              className="text-sm border border-solid border-transparent min-w-[120px]"
              leadingIcon={<Plus size={20} />}
            >
              {t('project.actions.addNew')}
            </Button>
          </div>
        )}
      </div>

      {/* MARK: Search and Filters */}
      <div className="flex flex-wrap justify-between flex-shrink-0 gap-3">
        <InputSearch
          searchText={searchText}
          setSearchText={handleSearchChange}
          placeholder={t('project.filters.searchPlaceholder')}
        />

        {/* MARK: Filters */}
        <div className="flex flex-wrap gap-3">
          <Select
            value={filters.companyName || ''}
            onChange={(value) =>
              handleCompanyChange(value === 'all' ? null : value)
            }
            placeholder={t('project.filters.company')}
            searchable
            clearable={isOrgAdmin}
            options={
              isOrgAdmin
                ? [
                    { value: 'all', label: t('common.all') },
                    ...companies.map((company: string) => ({
                      value: company,
                      label: company,
                    })),
                  ]
                : companies.map((company: string) => ({
                    value: company,
                    label: company,
                  }))
            }
            valueClassName="font-medium text-primary-900"
            placeholderClassName="font-medium text-primary-900"
            maxHeight={600}
          />
        </div>
      </div>

      {/* MARK: Paginated Table */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <Table
          columns={columns}
          data={projects}
          isLoading={isLoading}
          noDataMessage={noDataMessage}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          orderBy={sort.column}
          order={sort.direction}
          onSortChange={handleSortChange}
          onRowClick={(row) => handleActionClick('view-detail', row)}
          roundedTop
        />
      </div>

      {/* Dialog components */}
      {(dialogOpen || editingProject) && <ProjectDialog />}
      {deletingProject && <DeleteProjectDialog />}
    </div>
  )
}

export default ProjectListPage
